import * as React from "react";
import { FieldNav } from "../../components/FieldNav.js";
import { useStartDiagnostic } from "../../hooks/useStartDiagnostic.js";
import { useDocumentTitle } from "../../hooks/useDocumentTitle.js";
import { FieldFooter } from "../../components/FieldFooter.js";
import { getTestLandingVariant } from "../../lib/analytics.js";
import { readAssignedVariant, stampExposure, type TlVariant } from "../../lib/tl-experiment.js";
import { LeanBody, type LeanVariant } from "./LeanBody.js";
import { TestPrepInfo } from "./TestPrepInfo.js";
import { TestRelatedLinks } from "./TestRelatedLinks.js";
import { seoPhraseFor } from "./seoOverrides.js";
import type { TestLandingConfig } from "./config.js";
import "../../styles/landing-field.css";
import "../../styles/placement-intro-field.css";
import "../../styles/test-prep-field.css";

/**
 * The single reusable standardized-test landing template — the orchestrator of
 * the bridge-framing experiment (fun vs competition). The Field Day shell
 * (`<FieldNav />`, `.v-field` wrapper, `<FieldFooter />`) is constant chrome;
 * the page BODY is the experiment surface, switched by the pre-paint-assigned
 * PostHog flag. Both arms render through `<LeanBody>` — the experiment's one
 * axis is the hero+bridge FRAMING, so layout/CTA/art are held constant:
 *
 *  - `control`     → the proven `fun-bridge` body (prior experiment's winner).
 *  - `competition` → the same bridge layout reframed around competing.
 *
 *  - Client render: read the local assignment ONCE at mount (sticky for the
 *    pageview) and render exactly one body. The PostHog exposure registers in a
 *    mount effect, off the render path.
 *  - Prerender (`renderToString`, no window): stack BOTH bodies, each wrapped in
 *    `div[data-tl-body=…]`. The inline variant-gate `<style>` in index.html
 *    shows only the visitor's arm before the bundle loads — no flash, no blank,
 *    and bots/no-JS fall back to control. React then mounts and replaces the
 *    stack with the single assigned body (`createRoot` discards prerendered
 *    DOM, so no mismatch).
 *
 * All CTAs in every arm drive the ONE `useStartDiagnostic` instance — the
 * in-place placement auth gate (and its Lead event) fires without leaving the
 * page, identical mechanics across arms (the experiment's held-constant).
 */

/** Maps an experiment arm to its LeanBody render mode. `control` is the proven
 * fun-bridge baseline; `competition` reframes the same layout around competing. */
const BODY_VARIANT: Record<TlVariant, LeanVariant> = {
  control: "fun-bridge",
  competition: "competition-bridge",
};

export function TestLandingTemplate({ config }: { config: TestLandingConfig }): React.ReactElement {
  // Mirrors entry-server.tsx's TEST_LANDING_HEAD title (same seoOverrides
  // source), so the tab title matches what was prerendered.
  useDocumentTitle(`${seoPhraseFor(config.slug, config.shortName)} — Sports Academy Games`);
  // Tailors the placement to this page's test (coverage-maps profile).
  const diagnostic = useStartDiagnostic(config.slug);
  const isSSR = typeof window === "undefined";
  // Render from the pre-paint LOCAL assignment — a pure read, safe in a lazy
  // initializer (StrictMode double-invokes these in dev to flush out side
  // effects). PostHog's flag is pinned to this same value, so render and
  // measurement agree; and the read is sticky, so the arm a visitor is
  // reading never swaps out from under them.
  const [variant] = React.useState<TlVariant>(() =>
    isSSR ? "control" : (readAssignedVariant() ?? "control"),
  );

  React.useEffect(() => {
    // Measurement side effects live here, OFF the render path:
    //  - getTestLandingVariant() reads the flag through PostHog, which
    //    registers the $feature_flag_called exposure (posthog-js dedupes
    //    repeat calls per flag+value, so StrictMode's double effect still
    //    sends one event);
    //  - stampExposure persists slug+arm for Lead-time attribution (first
    //    exposure wins) before any CTA can open the auth gate.
    void getTestLandingVariant();
    stampExposure(config.slug, variant);
  }, [config.slug, variant]);

  return (
    <div className="v-field">
      <FieldNav ctaLabel="Start free" onCtaClick={() => void diagnostic.start()} />

      {isSSR ? (
        // Stack both arms for the pre-paint variant-gate. Only the control arm
        // renders its hero title as <h1>; the competition arm uses a styled <p>
        // (asH1={false}) so the static HTML a crawler reads has exactly one <h1>
        // (both look identical, so the shown arm still reads as a headline).
        <>
          <div data-tl-body="control">
            <LeanBody config={config} variant="fun-bridge" diagnostic={diagnostic} />
          </div>
          <div data-tl-body="competition">
            <LeanBody
              config={config}
              variant="competition-bridge"
              diagnostic={diagnostic}
              asH1={false}
            />
          </div>
        </>
      ) : (
        <LeanBody config={config} variant={BODY_VARIANT[variant]} diagnostic={diagnostic} />
      )}

      {/* Held-constant content + chrome, not part of the fun/competition
          framing experiment — rendered once regardless of arm, so they must
          live outside the isSSR stack above (never duplicated per
          `data-tl-body`). TestPrepInfo revives each config's topics/help/FAQ
          (the FAQ backs the FAQPage JSON-LD); TestRelatedLinks is the
          contextual practice/game link block. */}
      <TestPrepInfo config={config} />
      <TestRelatedLinks slug={config.slug} shortName={config.shortName} />

      <FieldFooter />
    </div>
  );
}
